<template>
  <div
    :class="isTopPick ? 'border-ocean-600' : 'border-neutral-200'"
    class="group relative flex flex-1 flex-col border rounded-lg shadow-xs hover:shadow-lg hover:bg-neutral-50 transition duration-250 font-inter"
  >
    <div
      v-if="isTopPick"
      class="absolute top-0 right-4 transform -translate-y-1/2 border border-ocean-600 bg-ocean-900 text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center space-x-1 z-10 whitespace-nowrap"
    >
      <fa
        aria-hidden="true"
        :icon="['fal', 'star']"
      />
      <span>{{ $t('search.top-pick-for-you') }}</span>
    </div>

    <div
      class="flex flex-1 flex-col w-full px-5 pb-4 pt-5 cursor-pointer relative"
      :data-testid="id || `search-result-${partner.uuid}`"
      @click="clickTile"
      @keydown.space.enter="clickTile"
    >
      <div class="flex space-x-3">
        <vetster-partner-photo
          :partner="partner"
          photo-size="size-12 lg:size-20"
        />
        <div class="flex flex-1 justify-between items-start min-w-0">
          <div class="max-w-[90%]">
            <p class="truncate font-semibold leading-4 lg:text-xl font-title">
              {{ fullName }}
            </p>
            <dl>
              <dt class="sr-only">
                {{ $t('vetster.designation') }}
              </dt>
              <dd class="leading-4 sm:min-h-[2.5em]">
                <span class="text-xs lg:text-sm text-neutral-500">
                  {{ fullDesignation }}
                </span>
              </dd>
            </dl>
            <div class="mt-2">
              <dl
                v-if="hasRatings"
                class="flex items-center w-full text-neutral-500 text-xs lg:text-sm"
              >
                <dt class="sr-only">
                  {{ $t('search.rating-and-reviews') }}
                </dt>
                <dd class="flex items-center justify-start">
                  <vetster-reviews-count :partner="partner" />
                </dd>
              </dl>
              <dl
                v-else
                class="text-xs lg:text-sm leading-4 w-full tracking-tight"
              >
                <dt class="sr-only">
                  {{ $t('search.rating-and-reviews') }}
                </dt>
                <dd class="flex items-center justify-start">
                  <fa
                    :icon="['fal', 'star']"
                    class="text-bubblegum-500"
                  />
                  <span class="leading-5 ml-2 text-neutral-500 text-xs lg:text-sm">
                    {{ $t('search.no-reviews-yet') }}
                  </span>
                </dd>
              </dl>
            </div>
          </div>
          <fa
            :icon="['fas', 'angle-right']"
            class="text-neutral-700 self-center"
          />
        </div>
      </div>

      <vetster-partner-badges
        :partner="partner"
        :is-in-state="isInState"
        :is-vcpr-enabled="hasVCPR"
        :is-vcpr-search="isVcprSearch"
        class="flex mt-3 lg:mt-4"
      />
    </div>

    <vetster-search-tile-banner
      v-if="canPurchaseSubscriptionPlan && (isFlatRateBookingPaygPricingExperiment || showSubscriptionBannerForPartner(timeslots))"
      class="flex-none"
    />

    <!-- Availability -->
    <section class="flex mt-auto flex-col justify-between bg-ocean-100 border-t border-neutral-200 rounded-b-lg p-3 lg:px-5 lg:py-4">
      <div class="flex flex-col space-y-2 lg:space-y-4">
        <vetster-search-date-button
          v-for="(appointment, i) in timeslots"
          :key="`${partner.uuid}-${i}`"
          :appointment="appointment"
          :timezone="timezone"
          @select-timeslot="selectTimeslot(appointment)"
        />
        <template v-if="timeslots.length > 0">
          <vetster-search-date-button
            v-for="i in hiddenSlotsCount"
            :key="`${i}-hidden`"
            class="hidden sm:flex opacity-0"
            :appointment="timeslots[0]"
            :timezone="timezone"
            disabled
          />
        </template>
      </div>
      <button
        type="button"
        class="mt-2 py-1 lg:py-0 lg:mt-4 text-sm leading-4 text-center text-ocean-950 underline underline-offset-2"
        @click="clickTile"
      >
        {{ $t('search.see-more-timeslots') }}
      </button>
    </section>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronRight,
  faFire,
  faPrescription,
  faSpa,
  faSparkles,
  faStar,
  faUsdCircle,
} from '@fortawesome/pro-light-svg-icons';
import {
  faAngleRight,
  faPaw,
  faQuestionCircle,
  faStar as fasStar,
} from '@fortawesome/pro-solid-svg-icons';
import { mapGetters } from 'vuex';

import VetsterSearchDateButton from '@/brand-components/GuidedSearch/VetsterSearchDateButton';
import VetsterSearchTileBanner from '@/brand-components/GuidedSearch/VetsterSearchTileBanner';
import VetsterPartnerBadges from '@/brand-components/VetsterPartnerBadges';
import VetsterPartnerPhoto from '@/brand-components/VetsterPartnerPhoto';
import VetsterReviewsCount from '@/brand-components/VetsterReviewsCount';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import usePartnerHelpers from '@/composables/use-partner-helpers';
import useSearchData from '@/composables/use-search-data';
import useSearchHelpers from '@/composables/use-search-helpers';
import useSmartTime from '@/composables/use-smart-time';
import Designation from '@/enums/Designation';

library.add({
  faAngleRight,
  faChevronRight,
  faFire,
  faPaw,
  faPrescription,
  faQuestionCircle,
  faSpa,
  faSparkles,
  faStar,
  fasStar,
  faUsdCircle,
});

export default {
  name: 'VetsterSearchTile',

  components: {
    VetsterPartnerBadges,
    VetsterPartnerPhoto,
    VetsterReviewsCount,
    VetsterSearchDateButton,
    VetsterSearchTileBanner,
  },

  props: {
    id: {
      type: String,
      default: undefined,
    },
    isInState: {
      type: Boolean,
      default: false,
    },
    isTopPick: {
      type: Boolean,
      default: false,
    },
    isVcprEnabled: {
      type: Boolean,
      default: false,
    },
    maxTimeslotsCount: {
      type: Number,
      required: true,
    },
    result: {
      type: Object,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const {
      fullDesignation,
      fullName,
    } = usePartnerHelpers(props.result.feature);
    const {
      getAppointmentDate,
      getAppointmentTime,
      getAppointmentTimeRange,
    } = useAppointmentDatetimeFormat();
    const { getLocationName } = useSearchHelpers();
    const { validDateFrom } = useSmartTime();
    const {
      isFlatRateBookingPaygPricingExperiment,
      showSubscriptionBannerForPartner,
    } = useSearchData();

    return {
      fullDesignation,
      fullName,
      getAppointmentDate,
      getAppointmentTime,
      getAppointmentTimeRange,
      getLocationName,
      isFlatRateBookingPaygPricingExperiment,
      showSubscriptionBannerForPartner,
      validDateFrom,
    };
  },

  computed: {
    ...mapGetters([
      'isGlobalUser',
      'getSiteLocale',
    ]),
    ...mapGetters('search', [
      'getInputs',
      'isVcprSearch',
    ]),
    ...mapGetters('subscriptions', ['canPurchaseSubscriptionPlan']),
    canPrescribe() {
      return this.isVcprEnabled && !this.isGeoRestricted;
    },
    hasVCPR() {
      return this.canPrescribe && this.isVet;
    },
    hasRatings() {
      return this.partner.rating_count > 0;
    },
    hiddenSlotsCount() {
      return Math.max(this.maxTimeslotsCount - this.timeslots.length, 0);
    },
    isGeoRestricted() {
      return this.isVet
        && this.getSiteLocale !== 'en-GB'
        && !this.isInState;
    },
    isVet() {
      return this.partner.designation === Designation.Veterinarian;
    },
    partner() {
      return this.result.feature;
    },
    timeslots() {
      return this.result.timeslots;
    },
  },

  methods: {
    async clickTile() {
      document.querySelectorAll('.tippy-popper').forEach((el) => el.remove());
      this.$emit('selected', this.result);
    },
    selectTimeslot(timeslot) {
      this.$emit('selected', this.result, timeslot);
    },
  },
};
</script>
